import { type ReactNode, type Ref } from 'react';
import { Pause, Play } from 'lucide-react';
import {
  formatDuration,
  formatTrackNumber,
  type Track,
} from '@/types';
import { isPlayable } from '@/lib/api';
import { usePlayer } from '@/hooks/usePlayer';
import { TrackListSkeleton } from '@/components/ui/skeleton';

type Props = {
  tracks: Track[];
  /** Album/release artist (header); paired with compilation for ZG strip */
  albumArtist?: string;
  /**
   * Compilations / VA: omit a "Zachary Gray" row prefix. All other releases
   * show `track.artist` whenever the track carries one.
   */
  isCompilation?: boolean;
  /** Rendered after track rows inside the mobile track scrollport */
  afterTracks?: ReactNode;
  /** Show track-row skeletons while the full release is fetching */
  loading?: boolean;
  /** Scrollport used for equal row-height fitting */
  scrollRef?: Ref<HTMLDivElement>;
};

function isZacharyGrayArtist(name: string): boolean {
  return /^zachary gray$/i.test(name.trim());
}

export function TrackList({
  tracks,
  albumArtist,
  isCompilation = false,
  afterTracks,
  loading = false,
  scrollRef,
}: Props) {
  const {
    toggleTrack,
    currentIndex,
    playing,
    currentTime,
    tracks: ctxTracks,
    radioMode,
  } = usePlayer();
  const activeTracks = !radioMode && ctxTracks.length ? ctxTracks : tracks;
  const releaseArtist = (albumArtist || 'Zachary Gray').trim();
  const compilation =
    isCompilation || /^various artists$/i.test(releaseArtist);

  return (
    <>
      <div className="track-list">
        <div className="track-scroll" ref={scrollRef}>
          {tracks.length > 0 ? (
            tracks.map((track) => {
              const playable = isPlayable(track);
              const active = activeTracks[currentIndex];
              const isCurrent =
                !radioMode &&
                Boolean(active) &&
                active!.id === track.id &&
                playing;
              const showTime =
                !radioMode && active?.id === track.id ? currentTime : 0;
              const trackArtist = track.artist?.trim();
              // Omit when it matches the release header artist (already shown above).
              // Compilations: strip "Zachary Gray — …"; keep other guests.
              const sameAsReleaseArtist =
                Boolean(trackArtist) &&
                trackArtist!.toLowerCase() === releaseArtist.toLowerCase();
              const showArtistPrefix =
                Boolean(trackArtist) &&
                !sameAsReleaseArtist &&
                !/^various artists$/i.test(trackArtist!) &&
                !(compilation && isZacharyGrayArtist(trackArtist!));
              const title = showArtistPrefix
                ? `${trackArtist} — ${track.name}`
                : track.name;

              return (
                <div
                  key={`${track.id}-${track.position}`}
                  className="track-row"
                  data-playing={isCurrent ? 'true' : undefined}
                  onClick={() =>
                    playable &&
                    toggleTrack(track.position, { adoptPending: true })
                  }
                  onKeyDown={(e) => {
                    if (!playable) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleTrack(track.position, { adoptPending: true });
                    }
                  }}
                  role={playable ? 'button' : undefined}
                  tabIndex={playable ? 0 : undefined}
                >
                  <button
                    type="button"
                    className="track-play"
                    disabled={!playable}
                    aria-label={isCurrent ? `Pause ${track.name}` : `Play ${track.name}`}
                    title={isCurrent ? 'Pause' : 'Play'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (playable) {
                        toggleTrack(track.position, { adoptPending: true });
                      }
                    }}
                  >
                    {isCurrent ? (
                      <Pause size={14} fill="currentColor" />
                    ) : (
                      <Play size={14} fill="currentColor" />
                    )}
                  </button>
                  <span className="track-no">{formatTrackNumber(track.position)}</span>
                  <span className="track-name">{title}</span>
                  <span className="track-time">
                    {showTime > 0 ? `${formatDuration(showTime)} / ` : ''}
                    {formatDuration(track.duration)}
                  </span>
                </div>
              );
            })
          ) : loading ? (
            <TrackListSkeleton rows={8} />
          ) : null}
          {afterTracks}
        </div>
      </div>
    </>
  );
}
